import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, Lock } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/portal";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      navigate(res.user.role === "admin" ? "/admin" : from === "/login" ? "/portal" : from, { replace: true });
    } else {
      setError(res.error);
    }
  };

  return (
    <div data-testid="page-login" className="min-h-screen bg-[#F9F9F6] grid grid-cols-1 lg:grid-cols-2">
      {/* Left visual */}
      <div className="relative hidden lg:block bg-[#1A1A1A] overflow-hidden">
        <div
          className="absolute inset-0 opacity-60"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1776625834115-467d0f22e499?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBsZWF0aGVyJTIwdGV4dHVyZSUyMG1hY3JvfGVufDB8fHx8MTc3NzIzNDQ3Nnww&ixlib=rb-4.1.0&q=85)",
            backgroundSize: "cover",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#1A1A1A] via-[#1A1A1A]/70 to-[#5C2018]/60" />
        <div className="relative h-full flex flex-col justify-between p-12 text-[#F9F9F6]">
          <Link to="/" data-testid="login-brand-link" className="flex items-center gap-3 w-fit">
            <div className="w-10 h-10 border border-[#F9F9F6] flex items-center justify-center font-serif">LRG</div>
            <div>
              <p className="font-serif text-base">LRG Holdings</p>
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#C7C7BF]">Botswana Leather</p>
            </div>
          </Link>
          <div>
            <p className="eyebrow text-[#E5C4A1]">Investor Portal</p>
            <h2 className="font-serif text-5xl tracking-tight leading-tight mt-5 max-w-md">
              Access the project. <span className="italic text-[#E5C4A1]">structured.</span>
            </h2>
            <p className="text-sm text-[#C7C7BF] mt-6 max-w-md leading-relaxed">
              Project structure, funding requirements, projected revenue streams, SEZA advantages, phase milestones and direct
              investor briefings.
            </p>
          </div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#A8A8A0]">SEZA-aligned · Lobatse, Botswana</p>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden inline-flex items-center gap-2 mb-8 text-[12px] uppercase tracking-[0.18em] text-[#666666]">
            ← Back to site
          </Link>
          <p className="eyebrow">Sign in</p>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight leading-tight mt-4">Investor Access.</h1>
          <p className="mt-3 text-sm text-[#666666]">For approved investors and the LRG Holdings team.</p>

          <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="login-form">
            <div>
              <label className="eyebrow">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="mt-2 w-full bg-transparent border-b border-[#1A1A1A] py-3 text-base focus:outline-none focus:border-[#5C2018]"
                placeholder="you@firm.com"
              />
            </div>
            <div>
              <label className="eyebrow">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="mt-2 w-full bg-transparent border-b border-[#1A1A1A] py-3 text-base focus:outline-none focus:border-[#5C2018]"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <p data-testid="login-error" className="text-sm text-[#5C2018] border-l-2 border-[#5C2018] pl-3 py-2 bg-[#5C2018]/5">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full inline-flex items-center justify-center gap-3 px-7 py-4 bg-[#5C2018] text-[#F9F9F6] text-[12px] uppercase tracking-[0.2em] font-semibold hover:bg-[#1A1A1A] transition-colors disabled:opacity-60"
            >
              {loading ? "Signing in…" : "Sign in"}
              <ArrowRight size={16} />
            </button>
          </form>

          <div className="mt-10 pt-8 border-t border-[#E5E5E0]">
            <p className="text-sm text-[#666666]">
              Don’t have access yet?{" "}
              <Link to="/register" data-testid="login-to-register" className="text-[#1A1A1A] border-b border-[#1A1A1A] pb-0.5 hover:text-[#5C2018] hover:border-[#5C2018]">
                Request investor access
              </Link>
            </p>
            <p className="mt-4 inline-flex items-center gap-2 text-xs text-[#666666]">
              <Lock size={12} /> Investor accounts require LRG admin approval before sign-in.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
